import React from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Toaster } from 'react-hot-toast'
import { RootState } from '../../store'
import { useTheme } from '../../hooks'
import Header from './Header'
import Sidebar from './Sidebar'
import LoadingSpinner from '../ui/LoadingSpinner'
import ErrorBoundary from '../ui/ErrorBoundary'

interface LayoutProps {
  children?: React.ReactNode
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { loading } = useSelector((state: RootState) => state.ui)
  const location = useLocation()
  const isFullBleedRoute = ['/tools/mindmaps'].some(path => location.pathname.startsWith(path))
  const { sidebarOpen } = useTheme()

  return (
    <ErrorBoundary>
      <div className="min-h-screen overflow-x-hidden bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
        {/* Sidebar */}
        <Sidebar />
        
        {/* Main Content */}
        <div className={`flex flex-col min-h-screen transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-0'}`}>
          {/* Header */}
          <Header />
          
          {/* Page Content */}
          <main className="flex-1 relative">
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-white to-purple-50/50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800" />
            
            {/* Content Container */}
            <div className={`relative z-10 ${isFullBleedRoute ? 'p-0' : 'p-4 lg:p-6'}`}>
              {children || <Outlet />}
            </div>
            
            {/* Global Loading Overlay */}
            {loading && (
              <div className="absolute inset-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm z-50 flex items-center justify-center">
                <LoadingSpinner size="lg" />
              </div>
            )}
          </main>
        </div>
        
        {/* Toast Notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: 'var(--toast-bg)',
              color: 'var(--toast-color)',
              borderRadius: '12px',
              border: '1px solid var(--toast-border)',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            },
            success: {
              iconTheme: {
                primary: '#10B981',
                secondary: '#FFFFFF',
              },
            },
            error: {
              iconTheme: {
                primary: '#EF4444',
                secondary: '#FFFFFF',
              },
            },
            loading: {
              iconTheme: {
                primary: '#3B82F6',
                secondary: '#FFFFFF',
              },
            },
          }}
        />
      </div>
    </ErrorBoundary>
  )
}

export default Layout